import { styled, css } from "styled-components";

export const CardsSectionWrapper = styled.div<{ $inverted: boolean }>`
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 96px 40px 48px 40px;
  background-color: ${({ theme, $inverted }) =>
    $inverted ? theme.colors.default[50] : "transparent"};

  ${({ theme }) => theme.breakpoints.md.query} {
    padding: 96px 24px;
  }
`;

export const CardsSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${({ theme }) => theme.content.maxWidth}px;
  flex-wrap: wrap;
  overflow-wrap: break-word; /* ✅ valid text wrapping */
  word-break: break-word;
`;

export const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`;

export const CardsContainer = styled.div`
  display: grid;
  /* Responsive columns. 295px min keeps 3 columns on desktop AND on
     iPad landscape (1024), while never allowing a 4th column on a
     1224px container. Below 930px the md query switches to a column. */
  grid-template-columns: repeat(auto-fit, minmax(295px, 1fr));
  width: 100%;
  padding: 48px 0;
  /* stretch (not center) so cards in the same row get equal height
     and aligned tops instead of being vertically staggered */
  align-items: stretch;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 20px;

  ${({ theme }) => theme.breakpoints.md.query} {
    flex-direction: column;
    padding: 24px 0;
    display: flex;
  }
`;

export const NumberedCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  padding: 48px 24px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1 0 0;
  flex-wrap: wrap;

  ${({ theme }) => theme.breakpoints.lg.query} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${({ theme }) => theme.breakpoints.sm.query} {
    grid-template-columns: repeat(1, 1fr);
    padding: 24px 12px;
  }
`;

export const ImageCardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between; /* evenly distribute within 1224px */
  /* Explicit width: with only margin auto, this wrapper shrinks to fit
     its content in the mobile/tablet column layout, making cards with
     short text render narrower than the others. */
  width: 100%;
  max-width: 1224px;
  margin: 0 auto;
  gap: 24px; /* adjust gap to your design */
`;

export const ImageCard = styled.div<{ $altStyling?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.default[100]};
  background: ${({ theme }) => theme.colors.base};
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  overflow: hidden;
  /* Fill the grid cell, capped at the design width. No fixed width —
     a fixed width forces the grid columns to overflow on tablet. */
  width: 100%;
  max-width: 386.96px;
  margin: 0 auto;
  max-height: 396px;

  ${({ theme }) => theme.breakpoints.lg.query} {
    /* Narrower cards wrap text onto more lines — let the card grow
       instead of clipping the bottom off. */
    max-height: none;
  }

  ${({ theme }) => theme.breakpoints.md.query} {
    max-width: 100%; /* single column layout */
    height: auto;
  }

  ${({ $altStyling }) =>
    $altStyling &&
    css`
      flex-direction: row;
      align-items: stretch;
      padding: 24px;
      gap: 24px;
      width: 100%;
      height: 200px;
      min-height: 0;

      ${ImageContainer} {
        flex: 0 0 40%;
        height: 100%;
        overflow: hidden;
        min-width: 0;
        max-width: 40%;
        align-self: stretch;
      }

      ${ImageCardTextContainer} {
        flex: 1 1 60%;
        padding: 0;
        gap: 8px;
        min-width: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
      }
    `}
`;

export const ImageCardTextContainer = styled.div`
  display: flex;
  padding: 0px 24px 48px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  min-width: 0; /* important inside flex parents */
`;

export const ImageCardDescription = styled.div`
  overflow: hidden;
  min-width: 0;

  p {
    display: -webkit-box;
    -webkit-line-clamp: 5; /* limits to 5 lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  overflow: hidden;
  flex-direction: column;

  /* Default vertical / stacked layout height */
  height: 144px;
  min-width: 0;
  aspect-ratio: 16 / 9;

  /* make images fill the container cleanly */
  img,
  > span,
  > picture {
    width: 100%;
    height: 100%;
    object-fit: cover; /* recommended for clean cropping */
    display: block;
  }
`;

export const Stat = styled.div`
  color: ${({ theme }) => theme.colors.primary.default};
`;

export const Card = styled.div`
  display: flex;
  width: 342px;
  margin: 0 auto; /* center within the grid cell */
  padding: 48px 24px;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.base};

  & span {
    color: ${({ theme }) => theme.colors.default[400]};
  }

  ${({ theme }) => theme.breakpoints.lg.query} {
    width: 280px;

    & > H4 {
      font-size: 16px;
    }
  }

  ${({ theme }) => theme.breakpoints.md.query} {
    width: 100%;

    & > H6 {
      font-size: 19px;
    }
  }
`;

export const NumberedCard = styled.div`
  max-height: 437px;
  height: 100%;
  max-width: 310px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 0 0;
  border-radius: 16px;
  border: ${({ theme }) => `1px solid ${theme.colors.default[200]}`};
  background: ${({ theme }) => theme.colors.default[50]};
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.lg.query} {
    width: 100%;
  }
`;

export const NumberedCardTextContainer = styled.div`
  display: flex;
  padding: 32px 32px 16px 32px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  width: 100%;

  & > p {
    color: ${({ theme }) => theme.colors.default[400]};
    font-size: 16px;
  }
`;

export const Number = styled.div`
  font-size: 130px;
  color: ${({ theme }) => theme.colors.default[400]};
  height: 120px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  justify-content: flex-end;
`;
